import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  BookOpenCheck,
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  Globe2,
  GraduationCap,
  HandHeart,
  HeartPulse,
  Landmark,
  LineChart,
  MapPinned,
  Monitor,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import { missionSiteFallback } from "../data/missionContent";
import PageBanner from "../components/PageBanner";

const iconMap = {
  HeartPulse,
  MonitorCog: Monitor,
  Monitor,
  GraduationCap,
  ShieldPlus: ShieldCheck,
  ShieldCheck,
  HandHeart,
  BookOpenCheck,
};

// CSS `object-position` derived from a CMS-driven focal point ({x,y} 0-100).
// Falls back to the legacy free-text `objectPosition` string (older fixtures)
// and finally to `center`. Detail wins over Pillar (matches the image source).
function focalPointStyle(detail, pillar) {
  const fp = detail?.focalPoint || pillar?.focalPoint;
  const xNum = Number(fp?.x);
  const yNum = Number(fp?.y);
  if (Number.isFinite(xNum) || Number.isFinite(yNum)) {
    const x = Math.max(0, Math.min(100, Number.isFinite(xNum) ? xNum : 50));
    const y = Math.max(0, Math.min(100, Number.isFinite(yNum) ? yNum : 50));
    return `${x}% ${y}%`;
  }
  const legacy = detail?.objectPosition || pillar?.objectPosition;
  if (typeof legacy === "string" && legacy.trim()) return legacy;
  return "center";
}

const supportIconMap = {
  ...iconMap,
  ArrowRight,
  BarChart3,
  Download,
  ExternalLink,
  FileText,
  Globe2,
  Landmark,
  LineChart,
  MapPinned,
  ReceiptText,
  Sparkles,
  Users,
};

const getConfiguredArray = (value, fallback = []) =>
  Array.isArray(value) && value.length ? value : fallback;

const getConfiguredIcon = (iconName, fallbackIcon) => supportIconMap[iconName] || fallbackIcon;

const normalizeJourneyItem = (item) =>
  Array.isArray(item)
    ? { title: item[0], text: item[1] }
    : { title: item?.title, text: item?.text || item?.description };

const formatProgramStat = (stat) => {
  if (typeof stat === "string") return stat;
  if (!stat) return "";
  const heading = [stat.value, stat.label].filter(Boolean).join(" ");
  return [heading, stat.note].filter(Boolean).join(": ");
};

const programImpactDetails = {
  healthcare: {
    image: "/assets/agm/polyclinic-consultation.jpg",
    location: "Polyclinic and field health support",
    proof: "Patient consultation, medicine access, specialty support and camp-based care.",
    outcome:
      "Families receive dependable health support without losing dignity at the point of care.",
    evidence: ["Polyclinic photographs", "Program reports", "Medical camp records"],
    focusStats: [
      "Accessible consultations",
      "General, dental and eye-care support",
      "Medicines and preventive care",
    ],
  },
  "future-skilling": {
    image: "/assets/agm/web/ifsl-lab-3.webp",
    location: "Institute of Future Skilling, Delhi",
    proof: "Modern computer labs, mentorship, digital tools and job-linked training pathways.",
    outcome:
      "Underserved youth gain employable skills, confidence and exposure to future-ready work.",
    evidence: ["IFS launch coverage", "Lab photographs", "Training partner references"],
    focusStats: [
      "2,000 learner ambition",
      "AI, design, coding, Tally and digital skills",
      "Girls and transgender learners included",
    ],
  },
  school: {
    image: "/assets/agm/champawat-school.jpg",
    location: "Swami Avdheshanand Public School, Champawat",
    proof:
      "Values-led school education, rural aspiration, computer exposure and classroom strengthening.",
    outcome:
      "Children gain discipline, confidence, digital readiness and a stronger path toward opportunity.",
    evidence: ["School photographs", "Public school listings", "Education reports"],
    focusStats: ["Rural education", "Computer-lab access", "Character and aspiration"],
  },
  mahakumbh: {
    image: "/assets/agm/mahakumbh-medical.jpg",
    location: "Mahakumbh seva camps",
    proof: "Food, shelter, medical support, blankets, transport and safety support for pilgrims.",
    outcome:
      "Large-scale spiritual gatherings become safer and more humane through organized seva.",
    evidence: ["Camp photographs", "Field records", "Public Mahakumbh references"],
    focusStats: [
      "Meals and food seva",
      "Pilgrim medical care",
      "Shelter, blankets and transport support",
    ],
  },
  "mass-wedding": {
    image: "/assets/agm/web/shagun-vivah-travtalk.jpeg",
    location: "The Zora Convention Centre, New Delhi",
    proof:
      "51 couples received public blessings, support cheques, household essentials and dignity-first celebration.",
    outcome:
      "Economically vulnerable families begin married life with honor, stability and community support.",
    evidence: ["Public media coverage", "Event photographs", "Partner references"],
    focusStats: ["51 couples supported", "Household essentials", "Ladli Foundation collaboration"],
  },
  vedant: {
    image: "/assets/agm/swamiji-vedant.jpg",
    location: "Vedant in public life",
    proof: "Swami ji's guidance gives AGM an ethic of oneness, discipline and dignity in action.",
    outcome:
      "Seva becomes more than aid: it becomes service performed without ego and with respect.",
    evidence: ["Public teachings", "Vedantic sources", "Program ethics"],
    focusStats: ["Equal vision", "Disciplined action", "Service without superiority"],
  },
};

const fieldLocations = [
  {
    place: "Delhi NCR",
    title: "Healthcare, skilling and dignity programs",
    text: "Polyclinic care, Institute of Future Skilling activity and Shagun Vivah Utsav reflect AGM's service presence in the capital region.",
    image: "/assets/agm/ifs-students.jpg",
  },
  {
    place: "Champawat, Uttarakhand",
    title: "Rural school education",
    text: "Swami Avdheshanand Public School strengthens rural aspiration through values-led education and digital exposure.",
    image: "/assets/agm/champawat-school.jpg",
  },
  {
    place: "Mahakumbh",
    title: "Relief and pilgrimage seva",
    text: "Food, medical care, shelter, blankets and transport support help protect pilgrims during massive spiritual gatherings.",
    image: "/assets/agm/mahakumbh-seva.jpg",
  },
  {
    place: "United States",
    title: "Diaspora support bridge",
    text: "U.S. supporters can participate through USD giving, professional mentorship, volunteer expertise and source-backed storytelling.",
    image: "/assets/agm/home-usa-times-square-yoga.jpg",
  },
];

const trustChecks = [
  {
    icon: ReceiptText,
    title: "Directed giving",
    text: "Supporters can choose healthcare, skilling, education, relief, dignity or Vedantic education rather than giving into an unclear pool.",
  },
  {
    icon: FileText,
    title: "Annual reports",
    text: "Reports create continuity across years and help donors understand the larger mission beyond one event.",
  },
  {
    icon: ShieldCheck,
    title: "Evidence before emotion",
    text: "Every major number gains credibility through photographs, records, reports, public coverage or partner documentation.",
  },
  {
    icon: Globe2,
    title: "U.S. donor clarity",
    text: "International supporters need USD payment clarity, program descriptions, source links and transparent nonprofit communication.",
  },
];

const donorJourney = [
  [
    "Choose a cause",
    "Pick the program area closest to your intent: health, education, skilling, relief, dignity or Vedant.",
  ],
  [
    "Give securely",
    "Use the region-appropriate payment path and direct support toward an active campaign.",
  ],
  [
    "See the field",
    "Review program photographs, annual reports, public updates and story-led evidence.",
  ],
  [
    "Stay connected",
    "Volunteer expertise, mentor students, share the work or build a local giving circle.",
  ],
];

const publicEvidence = [
  {
    label: "Institute of Future Skilling launch",
    source: "TheCSRUniverse",
    href: "https://thecsruniverse.com/articles/ladli-foundation-and-swami-avdheshanand-giri-ji-maharaj-launch-institute-of-future-skilling-to-empower-2-000-youth-in-digital-technologies",
    note: "Reports the IFS launch and the 2,000 youth training ambition.",
  },
  {
    label: "Shagun Vivah Utsav 2026",
    source: "TravTalk India",
    href: "https://travtalkindia.com/shagun-vivah-utsav-2026-organised-by-rashi-entertainment-ladli-foundation-hosts-51-couples-mass-wedding-in-delhi/",
    note: "Public coverage of 51 couples supported at The Zora Convention Centre.",
  },
  {
    label: "AGM U.S. nonprofit listing",
    source: "Charity Navigator",
    href: "https://www.charitynavigator.org/ein/333180376",
    note: "Public U.S. listing for donor due diligence and transparency checks.",
  },
  {
    label: "Swami ji and U.S. public presence",
    source: "Economic Times",
    href: "https://economictimes.indiatimes.com/news/india/indian-spiritual-leader-to-lead-international-yoga-day-celebrations-in-the-us/articleshow/92070217.cms",
    note: "Coverage of Swami ji's International Yoga Day presence in the U.S.",
  },
];

const SectionIntro = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
      {eyebrow}
    </p>
    <h2 className="text-3xl font-semibold leading-tight text-[#14201f] md:text-5xl">{title}</h2>
    {description && (
      <p className="mt-5 text-base leading-8 text-[#52615f] md:text-lg">{description}</p>
    )}
  </div>
);

const buildProgramImpactDetails = (program, detail, reports) => {
  const fallback = programImpactDetails[program.id] || {};
  const detailStats = getConfiguredArray(detail?.stats).map(formatProgramStat).filter(Boolean);
  const evidenceFromGallery = getConfiguredArray(detail?.gallery)
    .slice(0, 3)
    .map((item) => item.caption || item.alt)
    .filter(Boolean);
  const evidenceFromReports = getConfiguredArray(reports)
    .slice(0, 3)
    .map((report) => report.label)
    .filter(Boolean);

  return {
    image: detail?.heroImage || fallback.image || program.image,
    imageMobile: detail?.heroImageMobile || program.imageMobile,
    location: detail?.location || fallback.location || program.eyebrow,
    proof: detail?.deck || detail?.sections?.[0]?.body || fallback.proof || program.description,
    outcome: detail?.lead || fallback.outcome || program.description,
    evidence: getConfiguredArray(
      detail?.evidence,
      getConfiguredArray(
        evidenceFromGallery,
        getConfiguredArray(fallback.evidence, evidenceFromReports)
      )
    ),
    focusStats: getConfiguredArray(
      program.metrics,
      getConfiguredArray(detailStats, fallback.focusStats || [])
    ),
  };
};

const Impact = () => {
  const { t } = useTranslation();
  const { siteConfig, region } = useRegionalExperience();
  const [activeProgramId, setActiveProgramId] = useState("healthcare");

  const brand = { ...missionSiteFallback.brand, ...(siteConfig.brand || {}) };
  const impactPage = siteConfig.impactPage || {};
  const impactHero = impactPage.hero || {};
  const impactSections = impactPage.sections || {};
  const transparency = { ...missionSiteFallback.transparency, ...(siteConfig.transparency || {}) };
  const usaPresence = { ...missionSiteFallback.usaPresence, ...(siteConfig.usaPresence || {}) };
  const programs = getConfiguredArray(
    siteConfig.programPillars,
    missionSiteFallback.programPillars
  );
  const programDetails = getConfiguredArray(
    siteConfig.programDetails,
    missionSiteFallback.programDetails
  );
  const stats = getConfiguredArray(siteConfig.impactStats, missionSiteFallback.impactStats);
  const reports = getConfiguredArray(
    transparency.reports,
    missionSiteFallback.transparency.reports
  );
  const stories = getConfiguredArray(siteConfig.stories, missionSiteFallback.stories);
  const homepageAdditions = getConfiguredArray(
    siteConfig.homepageAdditions,
    missionSiteFallback.homepageAdditions
  );
  const programSummary = programs
    .slice(0, 5)
    .map((program) => program.eyebrow || program.title)
    .filter(Boolean)
    .join(", ");
  const activeProgram = useMemo(() => {
    return programs.find((program) => program.id === activeProgramId) || programs[0] || null;
  }, [activeProgramId, programs]);

  const programDetailsById = useMemo(
    () => new Map(programDetails.map((detail) => [detail.id, detail])),
    [programDetails]
  );

  const activeDetails = activeProgram
    ? buildProgramImpactDetails(activeProgram, programDetailsById.get(activeProgram.id), reports)
    : null;

  const trustCheckFallbackTitles = [
    t("impact.trustChecks.directedGiving.title"),
    t("impact.trustChecks.annualReports.title"),
    t("impact.trustChecks.evidenceBeforeEmotion.title"),
    t("impact.trustChecks.usDonorClarity.title"),
  ];
  const trustCheckFallbackTexts = [
    t("impact.trustChecks.directedGiving.text"),
    t("impact.trustChecks.annualReports.text"),
    t("impact.trustChecks.evidenceBeforeEmotion.text"),
    t("impact.trustChecks.usDonorClarity.text"),
  ];
  const trustSource = getConfiguredArray(impactPage.trustChecks, homepageAdditions);
  const trustCheckItems = getConfiguredArray(trustSource, trustChecks)
    .slice(0, 4)
    .map((item, index) => ({
      icon: getConfiguredIcon(item.icon, trustChecks[index]?.icon || ShieldCheck),
      title: item.title || trustCheckFallbackTitles[index],
      text: item.text || item.description || trustCheckFallbackTexts[index],
    }));

  const publicEvidenceFromPresence = getConfiguredArray(
    usaPresence.events,
    missionSiteFallback.usaPresence.events
  )
    .filter((event) => event.sourceUrl)
    .map((event) => ({
      label: event.title,
      source: event.sourceLabel,
      href: event.sourceUrl,
      note: event.text,
    }));
  const publicEvidenceItems = getConfiguredArray(
    impactPage.publicEvidence,
    getConfiguredArray(publicEvidenceFromPresence, publicEvidence)
  ).slice(0, 4);

  const fieldLocationsFromDetails = programDetails
    .map((detail) => {
      const program = programs.find((item) => item.id === detail.id);
      if (!program && !detail.location) return null;

      return {
        place: detail.location || program?.eyebrow,
        title: program?.title || detail.title || detail.location,
        text: detail.deck || detail.lead || program?.description,
        image: detail.heroImage || program?.image,
        imageMobile: detail.heroImageMobile || program?.imageMobile,
      };
    })
    .filter((item) => item?.place && item?.title && item?.text && item?.image);
  const fieldLocationItems = getConfiguredArray(
    impactPage.fieldLocations,
    getConfiguredArray(fieldLocationsFromDetails, fieldLocations)
  ).slice(0, 4);

  const configuredJourney = getConfiguredArray(impactPage.donorJourney);
  const donorJourneyItems = getConfiguredArray(configuredJourney, [
    {
      title: t("impact.donorJourney.chooseCause.title"),
      text: t("impact.donorJourney.chooseCause.text", {
        programSummary: programSummary || t("impact.donorJourney.chooseCause.fallbackPrograms"),
      }),
    },
    {
      title: t("impact.donorJourney.giveSecurely.title"),
      text: t("impact.donorJourney.giveSecurely.text", { paymentLabel: region.paymentLabel }),
    },
    {
      title: t("impact.donorJourney.seeField.title"),
      text: t("impact.donorJourney.seeField.text", { transparencyTitle: transparency.title }),
    },
    {
      title: t("impact.donorJourney.stayConnected.title"),
      text: homepageAdditions[1]?.text || t("impact.donorJourney.stayConnected.text"),
    },
  ]).map(normalizeJourneyItem);

  const supportSignalItems = getConfiguredArray(impactPage.supportSignals, [
    {
      icon: "BarChart3",
      title: t("impact.supportSignals.impactMetrics.title"),
      text: t("impact.supportSignals.impactMetrics.text", { count: stats.length }),
    },
    {
      icon: "ReceiptText",
      title: t("impact.supportSignals.givingPurpose.title"),
      text:
        homepageAdditions[0]?.text ||
        t("impact.supportSignals.givingPurpose.text"),
    },
    {
      icon: "FileText",
      title: t("impact.supportSignals.reports.title"),
      text: reports.length
        ? t("impact.supportSignals.reports.textWithReport", { reportLabel: reports[0].label })
        : t("impact.supportSignals.reports.textDefault"),
    },
    {
      icon: "Users",
      title: t("impact.supportSignals.volunteerDepth.title"),
      text:
        homepageAdditions[1]?.text ||
        t("impact.supportSignals.volunteerDepth.text"),
    },
  ]).slice(0, 4);
  const ctaConfig = impactPage.cta || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (programs.length && !programs.some((program) => program.id === activeProgramId)) {
      setActiveProgramId(programs[0].id);
    }
  }, [activeProgramId, programs]);

  return (
    <main className="max-w-[100vw] overflow-x-hidden bg-[#f7faf9] text-[#14201f]">
      <PageBanner
        {...(siteConfig.impactPage?.banner || {})}
        rightSlot={
          <div
            className="min-w-0 rounded-lg border border-white/16 bg-white/12 p-5 shadow-2xl shadow-black/25 backdrop-blur-md"
            style={{ maxWidth: "calc(100vw - 2rem)" }}
          >
            <div className="flex items-center justify-between gap-4 border-b border-white/14 pb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#f5c45e]">
                  {t("impact.banner.reportedHighlights")}
                </p>
                <h2 className="mt-2 text-2xl font-semibold">{t("impact.banner.atAGlance")}</h2>
              </div>
              <Sparkles className="h-7 w-7 text-[#f5c45e]" />
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="min-w-0 rounded-md border border-white/14 bg-black/16 p-4"
                >
                  <p className="text-3xl font-semibold text-[#f5c45e]">{stat.value}</p>
                  <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-white">
                    {stat.label}
                  </p>
                  <p className="mt-2 text-xs leading-5 text-white/68">{stat.detail}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs leading-5 text-white/58">
              {impactPage.statsNote || t("impact.banner.statsNote")}
            </p>
          </div>
        }
      />

      <section className="border-b border-[#dfe8e6] bg-white py-8">
        <div className="container mx-auto grid gap-3 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
          {trustCheckItems.map(({ icon: Icon, title, text }) => (
            <article key={title} className="rounded-lg border border-[#dfe8e6] p-5 shadow-sm">
              <Icon className="h-6 w-6 text-[#9d1f1c]" />
              <h2 className="mt-4 text-lg font-semibold text-[#14201f]">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-[#52615f]">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="programs" className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionIntro
              eyebrow={impactSections.programs?.eyebrow || t("impact.sections.programs.eyebrow")}
              title={
                impactSections.programs?.title ||
                t("impact.sections.programs.title")
              }
              description={
                impactSections.programs?.description ||
                t("impact.sections.programs.description", { shortName: brand.shortName })
              }
            />
            <Link
              to="/programs"
              className="inline-flex w-fit items-center gap-3 rounded-md border border-[#d7c2b6] bg-white px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#4b2418] transition hover:border-[#9d1f1c] hover:text-[#9d1f1c]"
            >
              {t("impact.sections.programs.viewAll")}
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-[0.34fr_0.66fr]">
            <div className="grid gap-3 lg:self-start">
              {programs.map((program) => {
                const Icon = iconMap[program.icon] || Landmark;
                const isActive = activeProgram?.id === program.id;
                return (
                  <button
                    key={program.id}
                    type="button"
                    onClick={() => setActiveProgramId(program.id)}
                    className={`flex items-center gap-4 rounded-lg border p-4 text-left transition ${
                      isActive
                        ? "border-[#9d1f1c] bg-[#9d1f1c] text-white shadow-lg shadow-[#9d1f1c]/20"
                        : "border-[#dfe8e6] bg-white text-[#14201f] hover:border-[#9d1f1c]"
                    }`}
                  >
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${
                        isActive ? "bg-white/14 text-[#f5c45e]" : "bg-[#f8efe5] text-[#9d1f1c]"
                      }`}
                    >
                      <Icon size={22} />
                    </span>
                    <span>
                      <span className="block text-xs font-bold uppercase tracking-[0.16em] opacity-70">
                        {program.eyebrow}
                      </span>
                      <span className="mt-1 block text-base font-semibold">{program.title}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {activeProgram && activeDetails && (
              <article className="overflow-hidden rounded-lg border border-[#d8c7ba] bg-white shadow-xl shadow-[#4b2418]/10">
                <div className="grid lg:grid-cols-[0.48fr_0.52fr] lg:items-stretch">
                  <div className="group relative overflow-hidden bg-[#f8efe5] p-4 lg:p-5">
                    <div className="relative aspect-[16/10] w-full overflow-hidden rounded-lg shadow-lg shadow-[#4b2418]/20 lg:aspect-[4/5] lg:max-h-[560px]">
                      <picture>
                        {activeDetails.imageMobile || activeProgram.imageMobile ? (
                          <source
                            srcSet={activeDetails.imageMobile || activeProgram.imageMobile}
                            media="(max-width: 767px)"
                          />
                        ) : null}
                        <img
                          src={activeDetails.image || activeProgram.image}
                          alt={activeProgram.title}
                          className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
                          style={{
                            objectPosition: focalPointStyle(activeDetails, activeProgram),
                          }}
                        />
                      </picture>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#241511]/90 to-transparent p-5 text-white">
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#f5c45e]">
                          {activeDetails.location}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-7 md:p-9">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#9d1f1c]">
                      {t("impact.pillar.activePillar")}
                    </p>
                    <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#241511] md:text-4xl">
                      {activeProgram.title}
                    </h2>
                    <p className="mt-4 text-base leading-8 text-[#52615f]">
                      {activeProgram.description}
                    </p>

                    <div className="mt-7 grid gap-3 md:grid-cols-2">
                      <div className="rounded-md bg-[#f8efe5] p-4">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#9d1f1c]">
                          {t("impact.pillar.fieldProof")}
                        </p>
                        <p className="mt-2 text-sm leading-7 text-[#4b2418]">
                          {activeDetails.proof}
                        </p>
                      </div>
                      <div className="rounded-md bg-[#f8efe5] p-4">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#9d1f1c]">
                          {t("impact.pillar.humanOutcome")}
                        </p>
                        <p className="mt-2 text-sm leading-7 text-[#33413f]">
                          {activeDetails.outcome}
                        </p>
                      </div>
                    </div>

                    <div className="mt-7 grid gap-5 md:grid-cols-2">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#60706d]">
                          {t("impact.pillar.keyMeasures")}
                        </p>
                        <div className="mt-3 space-y-3">
                          {(activeDetails.focusStats || []).map((item) => (
                            <div key={item} className="flex gap-3 text-sm leading-6 text-[#33413f]">
                              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#60706d]">
                          {t("impact.pillar.evidenceTrail")}
                        </p>
                        <div className="mt-3 space-y-3">
                          {(activeDetails.evidence || []).map((item) => (
                            <div key={item} className="flex gap-3 text-sm leading-6 text-[#33413f]">
                              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                      <Link
                        to={activeProgram.ctaHref || region.donationPath}
                        className="inline-flex items-center justify-center gap-3 rounded-md bg-[#9d1f1c] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-white transition hover:bg-[#7f1916]"
                      >
                        {activeProgram.ctaLabel || t("impact.pillar.supportThisWork")}
                        <ArrowRight size={17} />
                      </Link>
                      <Link
                        to="/volunteer"
                        className="inline-flex items-center justify-center gap-3 rounded-md border border-[#d8c7ba] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#241511] transition hover:border-[#9d1f1c] hover:text-[#9d1f1c]"
                      >
                        {t("impact.pillar.volunteerExpertise")}
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            )}
          </div>
        </div>
      </section>

      <section id="evidence" className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.42fr_0.58fr] lg:items-start">
            <SectionIntro
              eyebrow={impactSections.evidence?.eyebrow || t("impact.sections.evidence.eyebrow")}
              title={impactSections.evidence?.title || transparency.title}
              description={impactSections.evidence?.description || transparency.description}
            />

            <div className="grid gap-4">
              {reports.map((report) => (
                <a
                  key={report.label}
                  href={report.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col gap-5 rounded-lg border border-[#dfe8e6] bg-[#f7faf9] p-5 transition hover:-translate-y-1 hover:border-[#9d1f1c] hover:shadow-lg sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-white text-[#9d1f1c]">
                      <FileText size={24} />
                    </span>
                    <div>
                      <h3 className="text-xl font-semibold text-[#14201f]">{report.label}</h3>
                      <p className="mt-2 text-sm leading-6 text-[#52615f]">{report.note}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#9d1f1c]">
                    {t("impact.sections.evidence.download")}
                    <Download size={16} />
                  </span>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {publicEvidenceItems.map((item) => (
              <a
                key={`${item.href}-${item.label}`}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-[#dfe8e6] bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:border-[#9d1f1c] hover:shadow-lg"
              >
                <ExternalLink className="h-6 w-6 text-[#9d1f1c]" />
                <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-[#60706d]">
                  {item.source}
                </p>
                <h3 className="mt-2 text-xl font-semibold leading-tight text-[#14201f]">
                  {item.label}
                </h3>
                <p className="mt-3 text-sm leading-7 text-[#52615f]">{item.note}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f8efe5] py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            eyebrow={impactSections.locations?.eyebrow || t("impact.sections.locations.eyebrow")}
            title={
              impactSections.locations?.title ||
              t("impact.sections.locations.title")
            }
            description={
              impactSections.locations?.description ||
              t("impact.sections.locations.description", { shortName: brand.shortName })
            }
            align="center"
          />
          <div className="mt-12 grid gap-5 lg:grid-cols-4">
            {fieldLocationItems.map((item) => (
              <article
                key={item.place}
                className="group overflow-hidden rounded-lg border border-[#e5d1bf] bg-white shadow-sm"
              >
                <div className="aspect-[16/11] overflow-hidden">
                  <picture>
                    {item.imageMobile ? (
                      <source srcSet={item.imageMobile} media="(max-width: 767px)" />
                    ) : null}
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </picture>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[#9d1f1c]">
                    <MapPinned size={15} />
                    {item.place}
                  </div>
                  <h3 className="mt-4 text-xl font-semibold leading-tight text-[#14201f]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-[#52615f]">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div className="rounded-lg bg-[#4b2418] p-8 text-white md:p-10">
              <LineChart className="h-9 w-9 text-[#f5c45e]" />
              <h2 className="mt-6 text-3xl font-semibold leading-tight md:text-5xl">
                {impactSections.journey?.title || t("impact.sections.journey.title")}
              </h2>
              <p className="mt-5 text-base leading-8 text-white/76">
                {impactSections.journey?.description ||
                  t("impact.sections.journey.description", { shortName: brand.shortName })}
              </p>
            </div>

            <div className="grid gap-4">
              {donorJourneyItems.map(({ title, text }, index) => (
                <div
                  key={title}
                  className="flex gap-5 rounded-lg border border-[#dfe8e6] bg-white p-6 shadow-sm"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[#f8efe5] text-sm font-bold text-[#9d1f1c]">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="text-xl font-semibold text-[#14201f]">{title}</h3>
                    <p className="mt-2 text-sm leading-7 text-[#52615f]">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            eyebrow={impactSections.stories?.eyebrow || t("impact.sections.stories.eyebrow")}
            title={impactSections.stories?.title || t("impact.sections.stories.title")}
            description={
              impactSections.stories?.description ||
              t("impact.sections.stories.description")
            }
            align="center"
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {stories.map((story) => (
              <article
                key={story.title}
                className="overflow-hidden rounded-lg border border-[#dfe8e6] bg-[#f7faf9]"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={story.image}
                    alt={story.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9d1f1c]">
                    {story.eyebrow}
                  </p>
                  <h3 className="mt-3 text-2xl font-semibold leading-tight text-[#14201f]">
                    {story.title}
                  </h3>
                  <p className="mt-4 text-sm leading-7 text-[#52615f]">{story.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#241511] py-20 text-white md:py-28">
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] bg-[size:72px_72px] opacity-[0.18]" />
        <div className="container relative z-10 mx-auto grid gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#f5c45e]">
              {t("impact.sections.donorClarity.eyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight md:text-6xl">
              {impactSections.donorClarity?.title ||
                t("impact.sections.donorClarity.title")}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-8 text-white/76">
              {impactSections.donorClarity?.description ||
                t("impact.sections.donorClarity.description", {
                  regionLabel: region.label,
                  paymentLabel: region.paymentLabel,
                })}
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {supportSignalItems.map(({ icon, title, text }) => {
              const Icon = getConfiguredIcon(icon, ShieldCheck);
              return (
                <div
                  key={title}
                  className="rounded-lg border border-white/14 bg-white/10 p-6 backdrop-blur"
                >
                  <Icon className="h-7 w-7 text-[#f5c45e]" />
                  <h3 className="mt-5 text-xl font-semibold">{title}</h3>
                  <p className="mt-3 text-sm leading-7 text-white/72">{text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-[#d8c7ba] bg-white p-8 shadow-xl shadow-[#4b2418]/10 md:p-12">
            <div className="grid gap-8 lg:grid-cols-[1fr_0.82fr] lg:items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#9d1f1c]">
                  {ctaConfig.eyebrow || t("impact.cta.eyebrow")}
                </p>
                <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#14201f] md:text-5xl">
                  {ctaConfig.title || t("impact.cta.title")}
                </h2>
                <p className="mt-5 text-base leading-8 text-[#52615f]">
                  {ctaConfig.description ||
                    t("impact.cta.description", {
                      programSummary: programSummary || t("impact.cta.fallbackPrograms"),
                    })}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <Link
                  to={region.donationPath}
                  className="inline-flex items-center justify-center gap-3 rounded-md bg-[#9d1f1c] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#7f1916]"
                >
                  {ctaConfig.primaryLabel || region.paymentLabel}
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/volunteer"
                  className="inline-flex items-center justify-center gap-3 rounded-md border border-[#d8c7ba] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#241511] transition hover:border-[#9d1f1c] hover:text-[#9d1f1c]"
                >
                  {ctaConfig.secondaryLabel || t("impact.cta.volunteerWith", { shortName: brand.shortName })}
                  <HandHeart size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Impact;
